import {
  Geogroup,
  GeogroupNewsPost,
  GeogroupService,
  TFile,
  UserGeogroup,
} from '@geovelo-frontends/commons';
import loadable from '@loadable/component';
import { DialogActions, DialogContent, DialogProps, DialogTitle } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { AppContext } from '../../context';

const NewsCustomFormContent = loadable(() => import('./news-form-content'));

interface IValues {
  creator: 'user' | 'geogroup';
  content: string;
}

type TProps = Omit<DialogProps, 'onClose'> & {
  geogroup?: Geogroup;
  onClose: (post?: GeogroupNewsPost) => void;
  post: GeogroupNewsPost | null;
  userGeogroup?: UserGeogroup | null;
};

function NewsFormDialog({
  geogroup,
  userGeogroup,
  post,
  open,
  onClose,
  ...props
}: TProps): React.JSX.Element {
  const [photo, setPhoto] = useState<TFile | undefined>();
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isSubmitting, values, setValues, setFieldValue, handleSubmit } = useFormik<IValues>({
    initialValues: {
      creator: 'user',
      content: '',
    },
    validationSchema: Yup.object().shape({
      content: Yup.string().required(),
    }),
    onSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (!open) {
      setValues({ creator: 'user', content: '' });
      setPhoto(undefined);
    }
  }, [open]);

  async function onSubmit(
    { creator, content }: IValues,
    { setSubmitting }: FormikHelpers<IValues>,
  ) {
    if (!currentUser || !geogroup) return;

    setSubmitting(true);

    try {
      if (post) {
        const updatedNews = await GeogroupService.updateNewsPost(geogroup.id, post.id, {
          author_type:
            userGeogroup?.isAdmin && geogroup && creator === 'geogroup'
              ? 'AUTHOR_AS_GROUP'
              : 'AUTHOR_AS_MEMBER',
          text: content,
          photo: photo instanceof File || photo === null ? photo : undefined,
        });

        onClose(updatedNews);
      } else {
        const createdNews = await GeogroupService.addNewsPost(
          geogroup.id,
          {
            author_type:
              userGeogroup?.isAdmin && geogroup && creator === 'geogroup'
                ? 'AUTHOR_AS_GROUP'
                : 'AUTHOR_AS_MEMBER',
            text: content,
            photo: photo instanceof File ? photo : undefined,
            geo_group: geogroup.id,
          },
          undefined,
        );

        onClose(createdNews);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        t('geovelo.communities.news.form.not', { context: post ? 'updated' : 'created' }),
        { variant: 'error' },
      );
      setSubmitting(false);
    }
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={open} scroll="paper" {...props}>
      <DialogTitle>
        {t('geovelo.communities.news.form.title', { context: post ? 'update' : undefined })}
      </DialogTitle>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {open && (
            <NewsCustomFormContent
              geogroup={geogroup}
              isSubmitting={isSubmitting}
              newsPost={post}
              photo={photo}
              setFieldValue={setFieldValue}
              setPhoto={setPhoto}
              userGeogroup={userGeogroup}
              values={values}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" disabled={isSubmitting} onClick={() => onClose()} type="reset">
            {t(`commons.actions.close`)}
          </Button>
          <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
            {post ? t('commons.actions.update') : t('geovelo.communities.actions.post')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default NewsFormDialog;
